<script setup lang="ts">
// Header bell — Loop 2 of docs/plan-p0-loops.md. Polls unread-count every
// 60 s; opening the dropdown lazy-loads the 10 most recent notifications.
// Network failures are swallowed silently so the bell never breaks the header
// during a transient backend hiccup.

interface Notification {
  id: string
  kind: string
  title: string
  body: string
  targetUrl: string | null
  createdAt: string
  readAt: string | null
}

const { $api } = useNuxtApp()
const { t } = useI18n()

const unreadCount = ref(0)
const items = ref<Notification[]>([])
const open = ref(false)
const loading = ref(false)

async function fetchUnreadCount() {
  // Skip until the session is verified — the bell can mount off a stale
  // persisted actor during boot, and polling an authed endpoint while
  // guest just spams 401s in the console.
  if (useSession().state.value !== 'authenticated') return
  try {
    const res = await $api<{ count: number }>('/users/me/notifications/unread-count')
    unreadCount.value = res.count
  } catch {
    // silent — endpoint not implemented yet
  }
}

async function fetchRecent() {
  loading.value = true
  try {
    const res = await $api<{ notifications: Notification[] }>('/users/me/notifications', {
      query: { limit: 10 }
    })
    items.value = res.notifications
  } catch {
    items.value = []
  } finally {
    loading.value = false
  }
}

async function markRead(id: string) {
  try {
    await $api(`/users/me/notifications/${id}/read`, { method: 'POST' })
    const item = items.value.find(n => n.id === id)
    if (item && !item.readAt) {
      item.readAt = new Date().toISOString()
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
  } catch {
    // silent
  }
}

async function markAllRead() {
  try {
    await $api('/users/me/notifications/read-all', { method: 'POST' })
    for (const n of items.value) {
      n.readAt ??= new Date().toISOString()
    }
    unreadCount.value = 0
  } catch {
    // silent
  }
}

async function handleClick(n: Notification) {
  await markRead(n.id)
  if (n.targetUrl) {
    await navigateTo(n.targetUrl)
    open.value = false
  }
}

watch(open, (isOpen) => {
  if (isOpen) fetchRecent()
})

let interval: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  // Fetch as soon as the session verifies (bootstrap may still be running
  // at mount); the interval then keeps it fresh.
  watch(() => useSession().state.value, (s) => {
    if (s === 'authenticated') fetchUnreadCount()
  }, { immediate: true })
  interval = setInterval(fetchUnreadCount, 60_000)
})
onUnmounted(() => {
  if (interval) clearInterval(interval)
})
</script>

<template>
  <UPopover v-model:open="open">
    <UButton
      icon="i-lucide-bell"
      color="neutral"
      variant="ghost"
      data-testid="notification-bell"
      :aria-label="unreadCount > 0
        ? t('notifications.titleWithUnread', { n: unreadCount })
        : t('notifications.title')"
      class="relative"
    >
      <span
        v-if="unreadCount > 0"
        data-testid="notification-bell-count"
        class="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center font-bold"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </UButton>

    <template #content>
      <div class="w-80 max-h-[60vh] flex flex-col">
        <div class="px-4 py-3 border-b border-default flex items-center justify-between">
          <h3 class="font-semibold text-sm">
            {{ t('notifications.title') }}
          </h3>
          <UButton
            :label="t('notifications.markAll')"
            size="xs"
            color="neutral"
            variant="link"
            :disabled="unreadCount === 0"
            @click="markAllRead"
          />
        </div>

        <div class="overflow-y-auto flex-1">
          <p
            v-if="loading"
            class="px-4 py-6 text-center text-sm text-neutral-500"
          >
            {{ t('notifications.loading') }}
          </p>
          <p
            v-else-if="items.length === 0"
            class="px-4 py-6 text-center text-sm text-neutral-500"
          >
            {{ t('notifications.empty') }}
          </p>
          <button
            v-for="n in items"
            :key="n.id"
            type="button"
            class="w-full text-left px-4 py-3 hover:bg-elevated border-b border-default last:border-b-0 flex gap-3"
            @click="handleClick(n)"
          >
            <span
              :class="[
                'mt-1.5 w-2 h-2 rounded-full shrink-0',
                n.readAt ? 'bg-transparent border border-neutral-300' : 'bg-primary-500'
              ]"
            />
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium truncate">
                {{ n.title }}
              </p>
              <p class="text-xs text-neutral-500 truncate">
                {{ n.body }}
              </p>
            </div>
            <time class="text-xs text-neutral-400 shrink-0">
              {{ new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
            </time>
          </button>
        </div>

        <div class="px-4 py-3 border-t border-default">
          <UButton
            :label="t('notifications.seeAll')"
            to="/app/notifications"
            block
            color="neutral"
            variant="ghost"
            trailing-icon="i-lucide-arrow-right"
            @click="open = false"
          />
        </div>
      </div>
    </template>
  </UPopover>
</template>
